// VideoEditor.js - 视频编辑器组件,用于处理视频的拖拽、缩放和选择功能
import { useRef, useEffect, useState, useContext, useCallback } from 'react';
import { EditContext } from '@/context/EditContext';
import Moveable from 'react-moveable'; // 引入react-moveable库,用于实现元素的拖拽和变换
import Selecto from 'react-selecto'; // 引入react-selecto库,用于实现元素的选择功能
import './VideoEditor.less';
import { projectController } from '@/api/edit';

import VideoComponent from './video/VideoComponent';
import OriginalSubtitleComponent from './subtitle/OriginalSubtitleComponent';
import BilingualSubtitleComponent from './subtitle/BilingualSubtitleComponent';
/**
 * VideoEditor组件 - 视频编辑器的主要组件
 * 实现视频元素的拖拽、缩放、选择等功能
 */
export default function VideoEditor() {
  // 创建状态和引用
  const [isMouseDown, setIsMouseDown] = useState(false); // 跟踪鼠标按下状态
  const moveableRef = useRef(null); // Moveable组件的引用
  const [targets, setTargets] = useState([]); // 存储当前选中的目标元素
  // 存储句子拆分后的单词数组

  const [videoDragFlag, setVideoDragFlag] = useState(false);
  // 从EditContext获取全局状态和方法
  const {
    projectDetail, // 项目详情数据
    scaleRatio, // 视频编辑器的缩放比例
    bgItem, // 背景图片相关配置
    selectedAspectRatio, // 当前选中的视频宽高比
    audioPlayerRef, // 音频播放器引用
    setDiyLayout,
    setSubtitleBox,
    setTranslationSubtitleBox,
    subtitleShow,
    setSubtitleUpdateFlag,
    setTranslationSubtitleUpdateFlag,
    bilingualismSubtitleBox,
    setBilingualismSubtitleBox,
    userDetail,
    dimensions,
    setWatermarkPayPop,
  } = useContext(EditContext);

  const rootRef = useRef(null); // 根元素引用

  // 替换原有的静态guidelines定义
  const [guidelines, setGuidelines] = useState([]);

  // const [videoLoaded, setVideoLoaded] = useState(false);

  // 添加计算参考线的函数
  const calculateGuidelines = () => {
    const editor = document.querySelector('.video-editor');
    if (!editor) return;

    const width = editor.clientWidth;
    const height = editor.clientHeight;

    const newGuidelines = [
      // 水平参考线
      { pos: height * 0.1, type: 'horizontal' },
      { pos: height * 0.5, type: 'horizontal' },
      { pos: height * 0.9, type: 'horizontal' },
      // 垂直参考线
      { pos: width * 0.1, type: 'vertical' },
      { pos: width * 0.5, type: 'vertical' },
      { pos: width * 0.9, type: 'vertical' },
    ];

    setGuidelines(newGuidelines);
  };

  // 添加监听器以在组件挂载和窗口调整时更新参考线
  useEffect(() => {
    calculateGuidelines();

    const handleResize = () => {
      calculateGuidelines();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // DOM就绪状态
  const [domReady, setDomReady] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // 检查点击事件是否发生在video-editor或其子元素内
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setTargets([]);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  /**
   * 监听DOM元素是否就绪
   * 使用MutationObserver监听DOM变化
   */
  useEffect(() => {
    const checkDomReady = () => {
      const video1 = document.querySelector('#video1');
      if (video1) {
        setDomReady(true);
      }
    };

    const observer = new MutationObserver(checkDomReady);
    observer.observe(document.body, { childList: true, subtree: true });

    checkDomReady();
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      observer.disconnect();
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  /**
   * 初始化视频元素位置
   * 根据selectedLayout设置视频元素的样式
   */
  useEffect(() => {
    const video1 = document.querySelector('#video1');
    if (video1) {
      switch (selectedAspectRatio) {
        case 0:
          video1.style.width = `1920px`;
          video1.style.height = `1080px`;
          video1.style.transform = `translate(0px, 0px)`;
          break;
        case 1:
          video1.style.width = `1080px`;
          video1.style.height = `1080px`;
          video1.style.transform = `translate(0px, 0px)`;
          break;
        case 2:
          video1.style.width = `1080px`;
          video1.style.height = `1920px`;
          video1.style.transform = `translate(0px, 0px)`;
          break;
        default:
          video1.style.width = '100%';
          video1.style.height = '100%';
          video1.style.transform = `translate(0px, 0px)`;
      }

      // video1.style.borderRadius = `${layout[0].geometry.radius}px`;
    }
  }, [domReady, selectedAspectRatio, projectDetail?.landscapeLayoutId, dimensions]);

  useEffect(() => {
    if (projectDetail?.diyLayout) {
      video1.style.width = `${projectDetail?.diyLayout.width}px`;
      video1.style.height = `${projectDetail?.diyLayout.height}px`;
      video1.style.transform = `translate(${projectDetail?.diyLayout.x}px, ${projectDetail?.diyLayout.y}px)`;
    }
  }, [projectDetail?.diyLayout]);

  const handleSubtitleDrag = (target, x, y) => {
    setVideoDragFlag(true);
    if (subtitleShow === 3) {
      if (selectedAspectRatio === 0) {
        const obj = { ...bilingualismSubtitleBox[0] };
        obj.position169 = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[0] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
      if (selectedAspectRatio === 1) {
        const obj = { ...bilingualismSubtitleBox[0] };
        obj.position11 = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[0] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
      if (selectedAspectRatio === 2) {
        const obj = { ...bilingualismSubtitleBox[0] };
        obj.position916 = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[0] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
      if (selectedAspectRatio === 3) {
        const obj = { ...bilingualismSubtitleBox[0] };
        obj.positionCtm = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[0] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
    } else {
      if (selectedAspectRatio === 0) {
        setSubtitleBox((prev) => ({
          ...prev,
          position169: {
            x,
            y,
            isTop: true,
          },
        }));
      }
      if (selectedAspectRatio === 1) {
        setSubtitleBox((prev) => ({
          ...prev,
          position11: {
            x,
            y,
            isTop: true,
          },
        }));
      }
      if (selectedAspectRatio === 2) {
        setSubtitleBox((prev) => ({
          ...prev,
          position916: {
            x,
            y,
            isTop: true,
          },
        }));
      }
      if (selectedAspectRatio === 3) {
        setSubtitleBox((prev) => ({
          ...prev,
          positionCtm: {
            x,
            y,
            isTop: true,
          },
        }));
      }
    }
  };
  const handleNewSubtitleDrag = (target, x, y) => {
    setVideoDragFlag(true);
    if (subtitleShow === 3) {
      if (selectedAspectRatio === 0) {
        const obj = { ...bilingualismSubtitleBox[1] };
        obj.position169 = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[1] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
      if (selectedAspectRatio === 1) {
        const obj = { ...bilingualismSubtitleBox[1] };
        obj.position11 = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[1] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
      if (selectedAspectRatio === 2) {
        const obj = { ...bilingualismSubtitleBox[1] };
        obj.position916 = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[1] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
      if (selectedAspectRatio === 3) {
        const obj = { ...bilingualismSubtitleBox[1] };
        obj.positionCtm = {
          x,
          y,
          isTop: true,
        };
        bilingualismSubtitleBox[1] = obj;
        setBilingualismSubtitleBox(bilingualismSubtitleBox);
      }
    } else {
      if (selectedAspectRatio === 0) {
        setTranslationSubtitleBox((prev) => ({
          ...prev,
          position169: {
            x,
            y,
            isTop: true,
          },
        }));
      }
      if (selectedAspectRatio === 1) {
        setTranslationSubtitleBox((prev) => ({
          ...prev,
          position11: {
            x,
            y,
            isTop: true,
          },
        }));
      }
      if (selectedAspectRatio === 2) {
        setTranslationSubtitleBox((prev) => ({
          ...prev,
          position916: {
            x,
            y,
            isTop: true,
          },
        }));
      }
      if (selectedAspectRatio === 3) {
        setTranslationSubtitleBox((prev) => ({
          ...prev,
          positionCtm: {
            x,
            y,
            isTop: true,
          },
        }));
      }
    }
  };
  const handleSubtitleDragEnd = async (target) => {
    if (target.id.includes('subtitle')) {
      setSubtitleUpdateFlag(Date.now());
    }
    if (target.id.includes('bilingual')) {
      setTranslationSubtitleUpdateFlag(Date.now());
    }
    // changeSubtitleDetail();
    // setCanUndo(true);
    // setCanRedo(false);
  };

  /**
   * 自定义维度显示组件
   * 用于显示元素的位置信息
   */
  const DimensionViewable = {
    name: 'dimensionViewable',
    props: [],
    events: [],
    render(moveable) {
      const rect = moveable.getRect(); // 获取元素的位置和尺寸信息

      return (
        <div
          key={'dimension-viewer'}
          className={'moveable-dimension'}
          style={{
            position: 'absolute',
            left: `${rect.width / 2}px`,
            top: `${rect.height + 20}px`,
            background: '#4af',
            borderRadius: '2px',
            padding: `${2 / scaleRatio}px ${4 / scaleRatio}px`,
            color: 'white',
            fontSize: `${13 / scaleRatio}px`,
            whiteSpace: 'nowrap',
            fontWeight: 'bold',
            willChange: 'transform',
            transform: `translate(-50%, 0px)`,
          }}>
          {Math.round(rect.left)} x {Math.round(rect.top)} {/* 显示元素的坐标位置 */}
        </div>
      );
    },
  };

  /**
   * 鼠标事件处理函数
   */
  const handleMouseDown = () => {
    setIsMouseDown(true);
  };
  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  /**
   * 处理拖拽变化
   * @param {HTMLElement} target - 目标元素
   * @param {number} x - X坐标
   * @param {number} y - Y坐标
   */
  const handleChangeDrag = (target, x, y) => {
    setVideoDragFlag(true);
    setDiyLayout((prev) => ({
      ...prev,
      x,
      y,
    }));
  };

  /**
   * 处理大小调整变化
   * @param {HTMLElement} target - 目标元素
   * @param {number} width - 宽度
   * @param {number} height - 高度
   * @param {number} x - X坐标
   * @param {number} y - Y坐标
   */
  const handleChangeResize = (target, width, height, x, y) => {
    setDiyLayout((prev) => ({
      ...prev,
      width,
      height,
      x,
      y,
    }));
  };
  const handleLayoutUpdateEnd = (target) => {
    // let x=target.style.
    const width = parseFloat(target.style.width);
    const height = parseFloat(target.style.height);
    const matches = target.style.transform.match(/translate\(([-\d.]+)px, ([-\d.]+)px\)/);
    const x = parseFloat(matches[1]);
    const y = parseFloat(matches[2]);
    const projectItem = JSON.parse(sessionStorage.getItem('projectItem'));
    try {
      projectController.diyLayout({
        projectId: projectItem.id,
        body: {
          x,
          y,
          width,
          height,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const ShowSubtitleElementComponent = useCallback(() => {
    switch (subtitleShow) {
      case 0:
        return null;
      case 1:
        return <OriginalSubtitleComponent />;
      case 2:
        return <BilingualSubtitleComponent />;
      case 3:
        return (
          <>
            <OriginalSubtitleComponent />
            <BilingualSubtitleComponent />
          </>
        );
      default:
        return null;
    }
  }, [subtitleShow]);
  return (
    <div
      className="video-editor"
      ref={rootRef}
      style={{
        borderRadius: `${10 / scaleRatio}px`,
      }}>
      {/* 视频容器,包含可拖拽的视频元素 */}
      <div className="video-container" onMouseDown={handleMouseDown}>
        {userDetail?.subscription?.id === 1 && (
          <div className="watermarkBlock">
            <img
              className="watermark"
              src="https://web.talecast.ai/resource/resources/watermark/watermark.png"
            />
            <span className="watermarkMask" onClick={() => setWatermarkPayPop(true)} />
          </div>
        )}
        <img src={bgItem?.bgUrl} alt="" className="bgImg" crossOrigin="anonymous" />
        <audio ref={audioPlayerRef} className="target video" />
        <div>
          <VideoComponent />
          {ShowSubtitleElementComponent()}
        </div>
      </div>
      {/* Moveable组件配置,实现拖拽、缩放等功能 */}
      <Moveable
        ref={moveableRef}
        target={targets} // 当前选中���目标元素
        draggable={true} // 允许拖拽
        resizable={targets.some(
          (target) => !target.id.includes('subtitle') && !target.id.includes('bilingual'),
        )} // subtitle 元素不可改变大小
        rotatable={false} // 禁用旋转
        snappable={true} // 启用对齐吸附
        snapGridWidth={50} // 网吸附宽度
        snapGridHeight={50} // 网格吸附高度
        isDisplaySnapDigit={false} // 不显示吸附距离
        snapDigit={50} // 吸附精度
        ables={[DimensionViewable]} // 启用自定义维度显示
        props={{
          dimensionViewable: isMouseDown,
        }}
        horizontalGuidelines={guidelines.filter((g) => g.type === 'horizontal').map((g) => g.pos)} // 水平参考线
        verticalGuidelines={guidelines.filter((g) => g.type === 'vertical').map((g) => g.pos)} // 垂直参考线
        snapDirections={{
          // 定义吸附方向
          top: true,
          left: true,
          bottom: true,
          right: true,
          center: true,
          middle: true,
        }}
        elementSnapDirections={{
          // 元素间的吸附方向
          middle: true,
          center: true,
          top: true,
          left: true,
          bottom: true,
          right: true,
        }}
        // 拖拽时更新元素位置
        onDrag={({ target, transform }) => {
          target.style.transform = transform;
          // 从transform字符串中提取数字
          const matches = transform.match(/translate\(([-\d.]+)px, ([-\d.]+)px\)/);
          if (matches) {
            const x = parseFloat(matches[1]);
            const y = parseFloat(matches[2]);
            // 判断操作的dom不是subtitle才执行
            if (!target.id.includes('subtitle') && !target.id.includes('bilingual')) {
              handleChangeDrag(target, x, y);
            } else {
              if (target.id.includes('subtitle')) {
                handleSubtitleDrag(target, x, y);
              } else {
                handleNewSubtitleDrag(target, x, y);
              }
            }
          }
        }}
        // 调整大小时更新元素尺寸和位置
        onResize={({ target, width, height, drag }) => {
          target.style.width = `${width}px`;
          target.style.height = `${height}px`;
          target.style.transform = drag.transform;
          const matches = drag.transform.match(/translate\(([-\d.]+)px, ([-\d.]+)px\)/);
          if (matches) {
            const x = parseFloat(matches[1]);
            const y = parseFloat(matches[2]);
            if (!target.id.includes('subtitle') && !target.id.includes('bilingual')) {
              handleChangeResize(target, width, height, x, y);
            }
          }
        }}
        // 添加拖拽结束回调
        onDragEnd={({ target }) => {
          if (!videoDragFlag) return;
          if (!target.id.includes('subtitle') && !target.id.includes('bilingual')) {
            setTimeout(() => {
              handleLayoutUpdateEnd(target);
            });
          } else {
            handleSubtitleDragEnd(target);
          }
          setVideoDragFlag(false);
          // setCanUndo(true);
          // setCanRedo(false);
        }}
        // 添加调整大小结束回
        onResizeEnd={({ target }) => {
          if (!target.id.includes('subtitle') && !target.id.includes('bilingual')) {
            setTimeout(() => {
              handleLayoutUpdateEnd(target);
            });
          }
          // setCanUndo(true);
          // setCanRedo(false);
        }}
        // 吸附事件回调
        onSnap={() => {
          // 可以在这里添加吸附时的处理逻辑
        }}
        // 渲染时应用样式
        onRender={(e) => {
          e.target.style.cssText += e.cssText;
        }}
        // 群组渲染时应用样式
        onRenderGroup={(e) => {
          e.events.forEach((ev) => {
            ev.target.style.cssText += ev.cssText;
          });
        }}
      />

      {/* Selecto组件配置,实现元素选择功能 */}
      <Selecto
        dragContainer={rootRef.current}
        selectableTargets={['.target']}
        hitRate={0}
        selectByClick={true}
        selectFromInside={false}
        ratio={0}
        scale={scaleRatio}
        dragMode={false}
        scrollOptions={{
          container: rootRef.current,
          throttleTime: 30,
          threshold: 0,
        }}
        transformScale={scaleRatio}
        rootContainer={document.body}
        container={rootRef.current}
        filter={(node) => {
          return !targets.includes(node); // 如果元素已在中列表中则过滤掉
        }}
        onDragStart={(e) => {
          e.stop();
          setTargets([]);
          const moveable = moveableRef.current;
          const target = e.inputEvent.target;

          // 如果点击的是目标元素,立即选中并准备拖拽
          if (target.classList.contains('target')) {
            setTargets([target]);
            e.stop();

            // 等待目标更新后开始拖拽
            moveable.waitToChangeTarget().then(() => {
              moveable.dragStart(e.inputEvent);
            });
          } else if (
            moveable.isMoveableElement(target) ||
            targets.some((t) => t === target || t.contains(target))
          ) {
            e.stop();
          }
        }}
        onSelectEnd={(e) => {
          const moveable = moveableRef.current;
          const selected = e.selected;

          // 更新选中的目标元素
          setTargets(selected);

          // 如果是拖拽开始,则初始化拖拽
          if (e.isDragStart) {
            e.inputEvent.preventDefault();
            moveable.waitToChangeTarget().then(() => {
              moveable.dragStart(e.inputEvent);
            });
          }
        }}></Selecto>
    </div>
  );
}
